import { useQuery } from "@tanstack/react-query";
import type { PeopleTargetsReport, PersonPeriodsReport } from "@/lib/people-targets";
import { peopleTargetKeys } from "@/lib/query";

/** The error a failed report read should show, preferring the server's reason. */
async function readError(res: Response, fallback: string): Promise<never> {
  const body = await res.json().catch(() => null);
  const message =
    body !== null && typeof body === "object" && "error" in body ? String(body.error) : fallback;
  throw new Error(message);
}

/**
 * Every NOS person with their target schedule, from `GET /api/people-targets`
 * (admin only) — the Capacity report's list.
 *
 * A live read of NOS rather than a copy in our database, so it takes the same
 * long `staleTime` as the PTO report: refetching 157 rows on every window
 * focus spends a gateway call for nothing.
 */
export function usePeopleTargets() {
  return useQuery({
    queryKey: peopleTargetKeys.list(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<PeopleTargetsReport> => {
      const res = await fetch("/api/people-targets");
      if (!res.ok) return readError(res, "Could not load the Capacity report");
      const json = await res.json();
      return json.data;
    },
  });
}

/**
 * One person's monthly targets, from
 * `GET /api/people-targets/:personId/periods`.
 *
 * `enabled` keeps the call out of the way until a person is selected — the
 * whole reason the report is master/detail is that loading everybody's months
 * up front is 20 paginated gateway requests.
 */
export function usePersonPeriods(personId: string | null) {
  return useQuery({
    queryKey: peopleTargetKeys.periods(personId ?? ""),
    enabled: personId !== null && personId !== "",
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<PersonPeriodsReport> => {
      const res = await fetch(`/api/people-targets/${encodeURIComponent(personId ?? "")}/periods`);
      if (!res.ok) return readError(res, "Could not load this person's targets");
      const json = await res.json();
      return json.data;
    },
  });
}
